import { FormattedMessage, type IntlFormatters } from 'react-intl';

import { CurrencyInput } from 'ui-components/components/currency-input';
import { SearchableDropdownField } from 'ui-components/components/dropdown';
import { FlexRow } from 'ui-components/components/flex';
import { INLINE_MESSAGE_TYPES } from 'ui-components/components/inline-message';
import { MDash } from 'ui-components/components/mdash';

import { NumericColumnCell } from './numeric-input-row';
import { ProductRowLabel } from './product-row-label';
import { ReceivedProductsColumns } from './received-products-columns-constants';
import { messages } from './received-products.messages';
import styles from './received-products-columns.styles.module.less';
import { unspecifiedWarehouseLocation } from '../../../../../constants/unspecified-warehouse-location';
import { locationNameFromParts } from '../../../../../products/product-details/display-helpers';
import { TransferOrderProgressBar } from '../../transfer-order-page/components/transfer-order-progress-bar/transfer-order-progress-bar';
import type { UomInfo } from '../../components/create-transfer-order-drawer/components/product-search-input/types';
import type { WarehouseLocation } from '../types';

type ReceiveFormRow = {
  productId: number;
  sku: string;
  name: string;
  orderedQuantity: number;
  receivedQuantity: number | null;
  rejectedQuantity: number | null;
  inventoryLocationId: number;
  inventoryLocationIdError: boolean;
  accept: number;
  reject: number;
  cost: number;
  lot: string | null;
  isFirstProductRow: boolean;
  isLastProductRow: boolean;
  unitsOfMeasure?: UomInfo[];
  unitOfMeasureId?: number;
  orderedQuantityInUom?: number | null;
};

type RowProps = {
  index: number;
  original: ReceiveFormRow;
  values: Record<string, unknown>;
};

type ColumnProps = {
  id: string;
};

type CellProps = {
  value: unknown;
};

type CellRenderProps = {
  row: RowProps;
  column: ColumnProps;
  cell: CellProps;
};

export const getReceivedProductColumns = (
  updateData: (rowIndex: number, columnId: string, value: unknown) => void,
  warehouseLocations: WarehouseLocation[],
  openProductDetails: (productId: number) => void,
  formatMessage: IntlFormatters['formatMessage'],
  showAdvancedColumns = false,
  currencyCode = 'USD',
) => {
  const sortedWarehouseLocationOptions = warehouseLocations
    .map((loc) => ({
      value: String(loc.inventoryLocationId),
      label: locationNameFromParts(loc),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const handleUpdateWithDefaultLocation = (rowIndex: number, columnId: string, value: unknown, data: ReceiveFormRow) => {
    updateData(rowIndex, columnId, value);
    if (!data || !data.inventoryLocationId || data.inventoryLocationId === 0) {
      const unspecifiedLocationId = warehouseLocations.find((wl) => wl?.area === unspecifiedWarehouseLocation)?.inventoryLocationId;
      if (unspecifiedLocationId && data.inventoryLocationId === 0) {
        updateData(rowIndex, ReceivedProductsColumns.INVENTORY_LOCATION_ID, unspecifiedLocationId);
      }
    }
  };

  const advancedColumns = showAdvancedColumns
    ? [
        {
          Header: <FormattedMessage {...messages.lot} />,
          accessor: ReceivedProductsColumns.LOT,
          Cell: ({ row }: CellRenderProps) => (row.original.lot ? <span>{row.original.lot}</span> : <MDash />),
          width: 100,
        },
      ]
    : [];

  const columns = [
    {
      Header: <FormattedMessage {...messages.sku} />,
      accessor: ReceivedProductsColumns.SKU,
      Cell: ({ row }: CellRenderProps) => {
        const { name, sku, productId, isFirstProductRow } = row.original;
        return (
          <ProductRowLabel
            isFirstProductRow={isFirstProductRow}
            name={name}
            openProductDetails={openProductDetails}
            productId={productId}
            sku={sku}
          />
        );
      },
      width: 160,
    },
    {
      Header: <FormattedMessage {...messages.warehouseLocation} />,
      accessor: ReceivedProductsColumns.INVENTORY_LOCATION_ID,
      Cell: ({ row, column }: CellRenderProps) => {
        const { original } = row;

        const hasValue = original.inventoryLocationId && original.inventoryLocationId !== 0;
        const hasError =
          original.inventoryLocationIdError && !original.inventoryLocationId && (original.accept !== 0 || original.reject !== 0);

        return (
          <div className={styles['dropdown-styled']}>
            <SearchableDropdownField
              className={styles['warehouse-location-dropdown']}
              id={`location-dropdown-${row.index}`}
              inlineMessage={hasError ? formatMessage(messages.locationRequired) : undefined}
              inlineMessageType={hasError ? INLINE_MESSAGE_TYPES.error : undefined}
              isRequiredError={hasError}
              onItemSelected={(val) => updateData(row.index, column.id, val)}
              options={sortedWarehouseLocationOptions}
              placeholder={formatMessage(messages.select)}
              selectedItemValue={hasValue ? String(original.inventoryLocationId) : undefined}
            />
          </div>
        );
      },
      width: 100,
      minWidth: 180,
    },
    ...advancedColumns,
    {
      Header: <FormattedMessage {...messages.cost} />,
      accessor: ReceivedProductsColumns.COST,
      Cell: (props: CellRenderProps) => (
        <FlexRow className={styles['cost-input']}>
          <CurrencyInput
            className={styles['currency-input-small']}
            currencyCode={currencyCode}
            currencySymbolClassName={styles['currency-symbol']}
            disallowNegative
            onChange={(value) => updateData(props.row.index, props.column.id, parseFloat(String(value ?? 0)) || 0)}
            value={Number(props.cell.value) || 0}
          />
        </FlexRow>
      ),
      width: 100,
    },
    {
      Header: <FormattedMessage {...messages.accept} />,
      accessor: ReceivedProductsColumns.ACCEPT,
      Cell: (props: CellRenderProps) => {
        const { orderedQuantity, receivedQuantity, rejectedQuantity } = props.row.original;
        const remaining = Math.max(orderedQuantity - ((receivedQuantity ?? 0) + (rejectedQuantity ?? 0)), 0);
        return (
          <NumericColumnCell
            cell={{ value: Number(props.cell.value) || 0 }}
            column={props.column}
            row={{
              ...props.row,
              original: { ...props.row.original, orderedQuantity: remaining, receivedQuantity: 0, rejectedQuantity: 0 },
            }}
            updateData={(rowIndex, columnId, value) => handleUpdateWithDefaultLocation(rowIndex, columnId, value, props.row.original)}
          />
        );
      },
      width: 114,
    },
    {
      Header: <FormattedMessage {...messages.reject} />,
      accessor: ReceivedProductsColumns.REJECT,
      Cell: (props: CellRenderProps) => {
        const { orderedQuantity, receivedQuantity, rejectedQuantity } = props.row.original;
        const remaining = Math.max(orderedQuantity - ((receivedQuantity ?? 0) + (rejectedQuantity ?? 0)), 0);
        return (
          <NumericColumnCell
            cell={{ value: Number(props.cell.value) || 0 }}
            column={props.column}
            row={{
              ...props.row,
              original: { ...props.row.original, orderedQuantity: remaining, receivedQuantity: 0, rejectedQuantity: 0 },
            }}
            updateData={(rowIndex, columnId, value) => handleUpdateWithDefaultLocation(rowIndex, columnId, value, props.row.original)}
          />
        );
      },
      width: 114,
    },
    {
      Header: <FormattedMessage {...messages.received} />,
      accessor: ReceivedProductsColumns.TOTAL,
      Cell: ({ row }: CellRenderProps) => {
        const { receivedQuantity, rejectedQuantity, orderedQuantity } = row.original;

        const receivedItems = (Number(row.original.accept) || 0) + (receivedQuantity ?? 0);
        const rejectedItems = (Number(row.original.reject) || 0) + (rejectedQuantity ?? 0);

        return (
          <TransferOrderProgressBar
            receivedItems={receivedItems}
            rejectedItems={rejectedItems}
            renderWithoutItemsCopy
            totalItems={orderedQuantity}
          />
        );
      },
      width: 100,
    },
  ];

  return columns;
};
